import { useCallback, useMemo } from 'react';
import { GenericError } from 'shared/utils/errors/GenericError';
import { Maybe } from 'shared/utils/types';
import { CombinedError } from 'urql';

import { useCombinedErrorFormatters } from './useFormattedCombinedError/useFormattedCombinedError';

export const useGQLErrorFormatter = () => {
  const combinedErrorFormatter = useCombinedErrorFormatters();

  return useCallback(
    ({
      combinedError,
      errorLike,
    }: {
      combinedError?: Maybe<CombinedError>;
      /**
       * GQL errors require title, message, and code fields
       * passing any object containing those fields into `errorLike` will
       * treat it as an error and extract a new error from it.
       */
      errorLike?: Maybe<object>;
    }): Maybe<GenericError> => {
      const formattedError = combinedErrorFormatter(combinedError);

      if (formattedError) {
        return new GenericError({ ...formattedError, code: 'CombinedError' });
      }

      if (
        errorLike &&
        'title' in errorLike &&
        'message' in errorLike &&
        'code' in errorLike
      ) {
        const { title, message, code } = errorLike;
        if (
          typeof title === 'string' &&
          typeof message === 'string' &&
          typeof code === 'string'
        ) {
          return new GenericError({ title, message, code });
        }
      }

      return null;
    },
    [combinedErrorFormatter],
  );
};

export const useFormattedGQLErrors = ({
  combinedError,
  errorLike,
}: {
  combinedError?: Maybe<CombinedError>;
  errorLike?: Maybe<object>;
}): Maybe<GenericError> => {
  const formatter = useGQLErrorFormatter();
  return useMemo(
    () => formatter({ combinedError, errorLike }),
    [combinedError, errorLike, formatter],
  );
};
